import { useOutletContext } from 'react-router';
import { HardDrive, Globe, Mail, Database, Layers } from 'lucide-react';
import {
  UniversalLoadingState,
  Card,
  UsageBar,
  DateCell,
  NotificationCard,
} from '@afrilink/ui';
import { useServiceUsage, type Service } from '@/domains/hosting';
import {
  formatGB,
  formatLimit,
  formatMB,
  usagePercent,
} from '@/shared/utils/format';

export default function ServiceLimitsTab() {
  const service = useOutletContext<Service>();
  const { data: usage, isLoading } = useServiceUsage(service.id);

  if (isLoading) {
    return <UniversalLoadingState variant="spinner" caption="Loading usage…" />;
  }

  return (
    <div className="space-y-6">
      {!usage && (
        <NotificationCard
          tone="info"
          title="Live usage coming soon"
          body="Disk, bandwidth, mailbox and database counters will appear here once the cPanel usage feed is wired. Plan limits below are accurate."
        />
      )}
      <Card interactive={false}>
        <h3 className="border-b border-border px-6 py-3 text-sm font-semibold text-navy-primary">
          Usage
        </h3>
        <div className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
          <UsageRow
            icon={HardDrive}
            label="Disk"
            usedLabel={usage ? formatMB(usage.diskUsedMB) : '—'}
            totalLabel={formatMB(service.plan.diskMB)}
            percent={usage ? usagePercent(usage.diskUsedMB, usage.diskTotalMB) : 0}
          />
          <UsageRow
            icon={Globe}
            label="Bandwidth (this month)"
            usedLabel={usage ? formatMB(usage.bandwidthUsedMB) : '—'}
            totalLabel={formatGB(service.plan.bandwidthGB)}
            percent={usage ? usagePercent(usage.bandwidthUsedMB, usage.bandwidthTotalMB) : 0}
          />
          <UsageRow
            icon={Mail}
            label="Email accounts"
            usedLabel={usage ? usage.emailCount.toString() : '—'}
            totalLabel={formatLimit(service.plan.emailAccounts)}
            percent={
              usage && usage.emailLimit > 0
                ? usagePercent(usage.emailCount, usage.emailLimit)
                : 0
            }
            unlimited={service.plan.emailAccounts === -1}
          />
          <UsageRow
            icon={Database}
            label="Databases"
            usedLabel={usage ? usage.databaseCount.toString() : '—'}
            totalLabel={formatLimit(service.plan.databases)}
            percent={
              usage && usage.databaseLimit > 0
                ? usagePercent(usage.databaseCount, usage.databaseLimit)
                : 0
            }
            unlimited={service.plan.databases === -1}
          />
        </div>
        {usage && (
          <div className="border-t border-border px-6 py-3 text-xs text-muted-foreground inline-flex items-center gap-1">
            Snapshot from <DateCell value={usage.asOf} format="long" /> — refreshed hourly from the server.
          </div>
        )}
      </Card>

      <Card interactive={false}>
        <h3 className="border-b border-border px-6 py-3 text-sm font-semibold text-navy-primary">
          Plan limits
        </h3>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 px-6 py-5 sm:grid-cols-2">
          <Limit icon={HardDrive} label="Disk space"     value={formatMB(service.plan.diskMB)} />
          <Limit icon={Globe}     label="Monthly bandwidth" value={formatGB(service.plan.bandwidthGB)} />
          <Limit icon={Mail}      label="Email accounts" value={formatLimit(service.plan.emailAccounts)} />
          <Limit icon={Database}  label="Databases"      value={formatLimit(service.plan.databases)} />
          <Limit icon={Layers}    label="Addon domains"  value={formatLimit(service.plan.addonDomains)} />
          <Limit icon={Layers}    label="Subdomains"     value={formatLimit(service.plan.subdomains)} />
          <Limit icon={Layers}    label="Free SSL"       value={service.plan.hasFreeSSL ? 'Included' : 'Not included'} />
          <Limit icon={Layers}    label="Daily backups"  value={service.plan.hasDailyBackups ? 'Included' : 'Not included'} />
        </dl>
      </Card>
    </div>
  );
}

function UsageRow({
  icon: Icon,
  label,
  usedLabel,
  totalLabel,
  percent,
  unlimited,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  usedLabel: string;
  totalLabel: string;
  percent: number;
  unlimited?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="inline-flex items-center gap-2 text-foreground-soft">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {label}
        </span>
        <span className="text-xs tabular-nums text-navy-primary">
          {usedLabel} / {totalLabel}
        </span>
      </div>
      <UsageBar
        value={unlimited ? 4 : percent}
        max={100}
        ariaLabel={label}
      />
      {unlimited && (
        <p className="text-xs text-muted-foreground">Unlimited — bar shown for visual scale only.</p>
      )}
    </div>
  );
}

function Limit({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <dt className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-navy-primary">{value}</dd>
    </div>
  );
}
